/**
 * /admin/features — every coach-facing feature with its stage and vote counts.
 * Staff add and edit features here, and move them to Beta or Dropped. There is
 * no delete, and a key never changes. Counts only, no names.
 *
 * Aliases: admin features, feature votes admin, roadmap demand, feature stages
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Rocket } from "lucide-react";
import { useMemo, useState } from "react";

import { AdminDataTable } from "~/components/admin/data-table";
import { FeatureDialog } from "~/components/admin/feature-dialog";
import { AdminListPageHeader } from "~/components/admin/list-page-header";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { ConfirmationDialog } from "~/components/ui/confirmation-dialog";
import { EmptyState } from "~/components/ui/empty-state";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  adminFeaturesQueryOptions,
  updateFeatureFn,
} from "~/lib/functions/admin-features";
import type { AdminFeatureRow } from "~/lib/server/features";
import {
  listSearchSchema,
  listSortDefaults,
  type SortColumn,
  sortListRows,
} from "~/lib/shared/admin-list-sort";
import { FEATURE_FLAGS } from "~/lib/shared/feature-flags";
import { FEATURE_MODULE_LABELS } from "~/lib/shared/features/modules";
import {
  FEATURE_STAGE_LABELS,
  type FeatureStage,
} from "~/lib/shared/features/stage";
import { hasPermission } from "~/lib/shared/permissions";
import { nextSortSearch } from "~/lib/shared/sort";

const SORTS = [
  "feature",
  "module",
  "votes",
  "paying",
  "trial",
  "last30Days",
] as const;
type FeatureSort = (typeof SORTS)[number];

const SORT_DEFAULTS = listSortDefaults<FeatureSort>("votes", [
  "votes",
  "paying",
  "trial",
  "last30Days",
]);

const SORT_COLUMNS: Record<FeatureSort, SortColumn<AdminFeatureRow>> = {
  feature: { kind: "text", value: (row) => row.title },
  module: { kind: "text", value: (row) => FEATURE_MODULE_LABELS[row.module] },
  votes: { kind: "number", value: (row) => row.votes.total },
  paying: { kind: "number", value: (row) => row.votes.paying },
  trial: { kind: "number", value: (row) => row.votes.trial },
  last30Days: { kind: "number", value: (row) => row.votes.last30Days },
};

export const Route = createFileRoute("/admin/features/")({
  staticData: { title: "Features" },
  validateSearch: listSearchSchema(SORTS),
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "features:read"),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(adminFeaturesQueryOptions);
  },
  component: FeaturesAdminPage,
});

const STAGE_BADGE_VARIANTS: Record<
  FeatureStage,
  "outline-primary" | "scheduled" | "success" | "outline" | "cancelled"
> = {
  planned: "outline-primary",
  beta: "scheduled",
  new: "success",
  done: "outline",
  dropped: "cancelled",
};

const STAGE_DATE = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeZone: "UTC",
});

/** "New until" or "Done since", for a released feature. */
function stageDateLine(row: AdminFeatureRow): string | null {
  if (row.newUntil === null) return null;
  return `${row.stage === "done" ? "Done since" : "New until"} ${STAGE_DATE.format(row.newUntil)}`;
}

function flagLabel(flagKey: string): string {
  return Object.hasOwn(FEATURE_FLAGS, flagKey)
    ? flagKey
    : `${flagKey} (retired, reads as no flag)`;
}

const count = (
  id: Exclude<FeatureSort, "feature" | "module">,
  header: string,
  value: (row: AdminFeatureRow) => number,
): ColumnDef<AdminFeatureRow> => ({
  id,
  header,
  meta: { align: "end", className: "whitespace-nowrap" },
  cell: ({ row }) => (
    <span className="tabular-nums">{value(row.original)}</span>
  ),
});

const READ_COLUMNS: ColumnDef<AdminFeatureRow>[] = [
  {
    id: "feature",
    header: "Feature",
    cell: ({ row }) => (
      <div className="max-w-sm space-y-0.5 whitespace-normal">
        <p className="font-medium text-gray-900">{row.original.title}</p>
        <p className="text-xs wrap-anywhere text-gray-500">
          {row.original.key}
        </p>
      </div>
    ),
  },
  {
    id: "module",
    header: "Module",
    cell: ({ row }) => (
      <span className="text-gray-500">
        {FEATURE_MODULE_LABELS[row.original.module]}
      </span>
    ),
  },
  {
    id: "stage",
    header: "Stage",
    cell: ({ row }) => {
      const dateLine = stageDateLine(row.original);
      return (
        <div className="space-y-0.5">
          <Badge size="sm" variant={STAGE_BADGE_VARIANTS[row.original.stage]}>
            {FEATURE_STAGE_LABELS[row.original.stage]}
          </Badge>
          {dateLine && <p className="text-xs text-gray-500">{dateLine}</p>}
        </div>
      );
    },
  },
  {
    id: "flag",
    header: "Flag",
    cell: ({ row }) => (
      <span className="wrap-anywhere whitespace-normal text-gray-500">
        {row.original.flagKey ? flagLabel(row.original.flagKey) : "—"}
      </span>
    ),
  },
  count("votes", "Votes", (row) => row.votes.total),
  count("paying", "Paying", (row) => row.votes.paying),
  count("trial", "Trial", (row) => row.votes.trial),
  count("last30Days", "30 days", (row) => row.votes.last30Days),
];

function FeaturesAdminPage(): React.ReactElement {
  const { adminPermissions } = Route.useRouteContext();
  const canWrite = hasPermission(adminPermissions, "features:write");
  const { data } = useQuery(adminFeaturesQueryOptions);
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const sort = search.sort ?? SORT_DEFAULTS.sort;
  const direction = search.dir ?? SORT_DEFAULTS.direction(sort);

  // `null` adds a new feature; undefined means the dialog is closed.
  const [editing, setEditing] = useState<AdminFeatureRow | null | undefined>();
  const [movingToBeta, setMovingToBeta] = useState<AdminFeatureRow | null>(
    null,
  );
  const [dropping, setDropping] = useState<AdminFeatureRow | null>(null);

  const queryClient = useQueryClient();
  const move = useMutation({
    mutationFn: (input: { key: string; stage: "beta" | "dropped" }) =>
      updateFeatureFn({ data: input }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: adminFeaturesQueryOptions.queryKey,
      }),
    onSettled: () => {
      setMovingToBeta(null);
      setDropping(null);
    },
  });

  const rows = useMemo(
    () =>
      sortListRows(
        data ?? [],
        sort,
        direction,
        SORT_COLUMNS,
        (row) => row.title,
      ),
    [data, sort, direction],
  );

  const columns: ColumnDef<AdminFeatureRow>[] = canWrite
    ? [
        ...READ_COLUMNS,
        {
          id: "actions",
          header: () => <span className="sr-only">Actions</span>,
          meta: { align: "end" },
          cell: ({ row }) => (
            // `w-max` makes the column as wide as its buttons; a narrower column
            // lets them overflow into the votes beside it.
            <div className="ml-auto flex w-max gap-2">
              <Button onClick={() => setEditing(row.original)} size="sm">
                Edit
              </Button>
              {row.original.storedStage === "planned" && (
                <Button onClick={() => setMovingToBeta(row.original)} size="sm">
                  Set to Beta
                </Button>
              )}
              {(row.original.storedStage === "planned" ||
                row.original.storedStage === "beta") && (
                <Button onClick={() => setDropping(row.original)} size="sm">
                  Drop
                </Button>
              )}
            </div>
          ),
        },
      ]
    : READ_COLUMNS;

  const changeSort = (column: FeatureSort): void => {
    navigate({
      search: (previous) => ({
        ...previous,
        ...nextSortSearch({ sort, direction }, column, SORT_DEFAULTS),
      }),
    });
  };

  return (
    <div className="space-y-6 p-6">
      <AdminListPageHeader
        description="Every coach-facing feature, from What's coming to live, with how many people want it. Counts only, no names."
        newLabel="New feature"
        onNew={canWrite ? () => setEditing(null) : undefined}
        title="Features"
      />

      {move.error && (
        <p className="text-sm text-destructive" role="alert">
          {move.error.message}
        </p>
      )}

      <AdminDataTable
        columns={columns}
        data={rows}
        direction={direction}
        emptyState={
          <EmptyState
            description="Add the first feature, and it shows on What's coming."
            icon={Rocket}
            size="sm"
            title="No features yet"
          />
        }
        getRowId={(row) => row.key}
        onSortChange={changeSort}
        sort={sort}
        sorts={SORTS}
      />

      <p className="text-xs text-gray-500">
        "Paying" and "Trial" use each voter's NOVA plan today. The other votes
        come from coaches before a trial, after cancelling, or on free access.
        "30 days" counts the votes from the last 30 days.
      </p>

      {editing !== undefined && (
        <FeatureDialog
          feature={editing}
          key={editing?.key ?? "new"}
          onClose={() => setEditing(undefined)}
        />
      )}

      <ConfirmationDialog
        confirmText="Set to Beta"
        description={`Coaches who have it stop seeing it on What's coming. The app first checks that its app path opens a page and that no NOVA card still reads Coming soon.`}
        isLoading={move.isPending}
        onConfirm={() => {
          if (movingToBeta) {
            move.mutate({ key: movingToBeta.key, stage: "beta" });
          }
        }}
        onOpenChange={(open) => {
          if (!open) setMovingToBeta(null);
        }}
        open={movingToBeta !== null}
        title={`Set ${movingToBeta?.title ?? "this feature"} to Beta?`}
      />

      <ConfirmDialog
        confirmLabel="Drop feature"
        description="It leaves What's coming and every card for everyone, and a dropped feature stays dropped. Within 15 minutes its votes are deleted. Nobody is told."
        isLoading={move.isPending}
        onConfirm={() => {
          if (dropping) move.mutate({ key: dropping.key, stage: "dropped" });
        }}
        onOpenChange={(open) => {
          if (!open) setDropping(null);
        }}
        open={dropping !== null}
        title={`Drop ${dropping?.title ?? "this feature"}?`}
      />
    </div>
  );
}
